import * as React from 'react'

import Box from '@mui/material/Box'
import Radio from '@mui/material/Radio'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Alert from '@mui/material/Alert'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'

import { useFormFields } from './hooks'
import type { ICancelReason } from '@user/store/scheduling'
import type { ISalesFunnelClosedReasons } from '@user/store/salesFunnel'
import { CancelServicesSection } from '@user/components/SoldServices/CancelServices'

type Props = {
  entityId: number
  requireCancelReason: boolean
  cancelReasons: ICancelReason[]
  reasonDetail: ISalesFunnelClosedReasons
  showCancelServices: boolean
}

const optionLabelSx = { m: 0, px: 1.5, py: 1, width: '100%' }

export function ClosedLostFormFields(props: Props) {
  const {
    entityId,
    cancelReasons,
    requireCancelReason,
    showCancelServices,
    reasonDetail: { allowManualDisqualifyReasons, disqualifyReasons },
  } = props

  const {
    reasonField,
    customReasonRef,
    customReasonField,
    cancelReasonField,
    cancelReasonError,
    onFocusCustomReason,
    customReasonHelperText,
  } = useFormFields()

  return (
    <>
      <Typography variant="subtitle3">Add Reason</Typography>

      <RadioGroup {...reasonField} sx={{ gap: 1, mt: 1 }}>
        {disqualifyReasons.map((reason) => (
          <Paper key={reason.id} variant="outlined" sx={{ borderRadius: 1 }}>
            <FormControlLabel
              control={<Radio />}
              label={reason.reason}
              value={reason.id.toString()}
              sx={optionLabelSx}
              data-test-id={`closed-lost-reason-${reason.id}`}
            />
          </Paper>
        ))}

        {allowManualDisqualifyReasons && (
          <Paper variant="outlined" sx={{ borderRadius: 1 }}>
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label="Other (custom reason)"
              sx={optionLabelSx}
              data-test-id="closed-lost-reason-custom"
            />

            {allowManualDisqualifyReasons && reasonField.value === 'custom' && (
              <Box px={1.5} mb={1}>
                <TextField
                  {...customReasonField}
                  fullWidth
                  inputRef={customReasonRef}
                  onFocus={onFocusCustomReason}
                  inputProps={{ maxLength: 50 }}
                  placeholder="Enter Custom Reason"
                  helperText={customReasonHelperText}
                  data-test-id="closed-lost-reason-custom-input"
                />
              </Box>
            )}
          </Paper>
        )}
      </RadioGroup>

      {requireCancelReason && (
        <Stack mt={2}>
          <Typography variant="subtitle3" mb={1}>
            Select a cancellation reason*
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Non-billable estimates will be canceled automatically.
          </Alert>

          <TextField
            {...cancelReasonField}
            select
            fullWidth
            label="Not Serviced Reason"
            error={!!cancelReasonError}
            helperText={cancelReasonError}
            data-test-id="closed-lost-cancel-reason-select"
          >
            {cancelReasons.map((r) => (
              <MenuItem key={r.id} value={r.name}>
                {`${r.name} - ${r.description}`}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      {showCancelServices && (
        <CancelServicesSection
          opportunityId={entityId}
          showHeader
          showDivider
        />
      )}
    </>
  )
}
